import * as React from 'react';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { Plus, Calendar, X, Pencil } from 'lucide-react';
import { toast } from 'sonner';

import { Button } from '@/components/ui/Button';
import { Card, CardContent } from '@/components/ui/Card';
import { Badge } from '@/components/ui/Badge';
import { DataTable, type DataColumn } from '@/components/data/DataTable';
import { DateTime } from '@/components/data/DateTime';
import { ConfirmDialog } from '@/components/data/ConfirmDialog';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/Dialog';
import { IssueForm } from '@/features/newsletter/IssueForm';
import { ScheduleDialog } from '@/features/newsletter/ScheduleDialog';
import {
  useCancelNewsletterIssue,
  useNewsletterIssues,
} from '@/queries/newsletter';
import { useApiError } from '@/lib/useApiError';
import type {
  NewsletterIssue,
  NewsletterIssueState,
} from '@/schemas/admin/newsletter';

export const Route = createFileRoute('/_authed/newsletter/issues')({
  component: NewsletterIssuesScreen,
});

function NewsletterIssuesScreen() {
  const { t } = useTranslation('newsletter');
  const { t: tc } = useTranslation('common');
  const list = useNewsletterIssues();
  const cancelMutation = useCancelNewsletterIssue();
  const resolveError = useApiError();
  const navigate = useNavigate();

  const [creating, setCreating] = React.useState(false);
  const [editing, setEditing] = React.useState<NewsletterIssue | null>(null);
  const [scheduling, setScheduling] = React.useState<string | null>(null);
  const [cancelling, setCancelling] = React.useState<NewsletterIssue | null>(null);

  function stateTone(state: NewsletterIssueState): 'neutral' | 'info' | 'success' | 'warn' | 'danger' {
    switch (state) {
      case 'sent':
        return 'success';
      case 'scheduled':
        return 'info';
      case 'sending':
        return 'warn';
      case 'cancelled':
        return 'danger';
      default:
        return 'neutral';
    }
  }

  async function handleCancel() {
    if (!cancelling) return;
    try {
      await cancelMutation.mutateAsync(cancelling.id);
      toast.success(t('issues.cancel.saved'));
      setCancelling(null);
    } catch (err) {
      const resolved = resolveError(err);
      toast.error(resolved.message, resolved.recovery ? { description: resolved.recovery } : undefined);
    }
  }

  const columns: readonly DataColumn<NewsletterIssue>[] = [
    {
      id: 'subject',
      header: t('issues.table.subject'),
      cell: (r) => (
        <div className="flex flex-col">
          <span className="font-medium">{r.subject_en ?? '—'}</span>
          {r.subject_ar ? (
            <span dir="rtl" className="text-xs text-[color:var(--color-fg-muted)]">
              {r.subject_ar}
            </span>
          ) : null}
        </div>
      ),
    },
    {
      id: 'state',
      header: t('issues.table.state'),
      cell: (r) => (
        <Badge tone={stateTone(r.state)}>{t(`issues.states.${r.state}`)}</Badge>
      ),
      sortBy: (r) => r.state,
    },
    {
      id: 'scheduled',
      header: t('issues.table.scheduled'),
      cell: (r) => <DateTime value={r.scheduled_at} />,
      sortBy: (r) => (r.scheduled_at ? new Date(r.scheduled_at) : null),
    },
    {
      id: 'sent',
      header: t('issues.table.sent', { defaultValue: 'Sent' }),
      cell: (r) => <DateTime value={r.sent_at} />,
      sortBy: (r) => (r.sent_at ? new Date(r.sent_at) : null),
    },
    {
      id: 'created',
      header: t('issues.table.created'),
      // Show the absolute date so operators can disambiguate seeded rows that
      // share the same subject. The relative-time display is only useful when
      // the row is unique. F-AD-23.
      cell: (r) =>
        r.created_at ? (
          <span dir="ltr" className="font-mono text-xs">
            {r.created_at.slice(0, 10)}
          </span>
        ) : (
          <span className="text-[color:var(--color-fg-muted)]">—</span>
        ),
      sortBy: (r) => (r.created_at ? new Date(r.created_at) : null),
    },
    {
      id: 'actions',
      header: <span className="sr-only">{tc('table.actions', { defaultValue: 'Actions' })}</span>,
      align: 'end',
      cell: (r) => (
        <div className="flex justify-end gap-1">
          {(r.state === 'draft' || r.state === 'scheduled') ? (
            <Button size="sm" variant="ghost" onClick={() => setEditing(r)}>
              <Pencil className="size-4" aria-hidden />
              <span className="sr-only md:not-sr-only md:text-xs">{t('issues.edit')}</span>
            </Button>
          ) : null}
          {r.state === 'draft' ? (
            <Button size="sm" variant="secondary" onClick={() => setScheduling(r.id)}>
              <Calendar className="size-4" aria-hidden />
              <span className="sr-only md:not-sr-only md:text-xs">{t('issues.schedule.open')}</span>
            </Button>
          ) : null}
          {r.state === 'scheduled' ? (
            <Button size="sm" variant="ghost" onClick={() => setCancelling(r)}>
              <X className="size-4" aria-hidden />
              <span className="sr-only md:not-sr-only md:text-xs">{t('issues.cancel.open')}</span>
            </Button>
          ) : null}
        </div>
      ),
    },
  ];

  return (
    <div className="space-y-6">
      <header className="flex flex-wrap items-start justify-between gap-3">
        <div className="space-y-1">
          <h1 className="text-2xl font-semibold tracking-tight">{t('issues.title')}</h1>
          <p className="max-w-3xl text-sm text-[color:var(--color-fg-muted)]">{t('issues.subtitle')}</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Button
            variant="ghost"
            onClick={() => void navigate({ to: '/newsletter/subscribers' })}
          >
            {t('subscribers.title')}
          </Button>
          <Button onClick={() => setCreating(true)}>
            <Plus className="size-4" aria-hidden />
            {t('issues.new')}
          </Button>
        </div>
      </header>

      <Card>
        <CardContent className="pt-6">
          <DataTable<NewsletterIssue>
            data={list.data ?? []}
            columns={columns}
            rowKey={(r) => r.id}
            loading={list.isLoading}
            error={list.error ? resolveError(list.error) : null}
            empty={{ title: t('issues.empty'), body: t('issues.emptyBody') }}
            initialSort={{ id: 'created', direction: 'desc' }}
          />
        </CardContent>
      </Card>

      <Dialog open={creating} onOpenChange={setCreating}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>{t('issues.new')}</DialogTitle>
            <DialogDescription>
              {t('issues.newBody', {
                defaultValue:
                  'Draft a new newsletter issue. Schedule or send it once content is ready.',
              })}
            </DialogDescription>
          </DialogHeader>
          <IssueForm onSaved={() => setCreating(false)} onCancel={() => setCreating(false)} />
        </DialogContent>
      </Dialog>

      <Dialog open={Boolean(editing)} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>{t('issues.edit')}</DialogTitle>
            <DialogDescription>
              {t('issues.editBody', {
                defaultValue:
                  'Update subject and body. Editing a scheduled issue keeps its send time.',
              })}
            </DialogDescription>
          </DialogHeader>
          {editing ? (
            <IssueForm
              issue={editing}
              onSaved={() => setEditing(null)}
              onCancel={() => setEditing(null)}
            />
          ) : null}
        </DialogContent>
      </Dialog>

      <ScheduleDialog
        issueId={scheduling}
        open={Boolean(scheduling)}
        onOpenChange={(o) => !o && setScheduling(null)}
      />

      <ConfirmDialog
        open={Boolean(cancelling)}
        onOpenChange={(o) => !o && setCancelling(null)}
        title={t('issues.cancel.title')}
        description={t('issues.cancel.body')}
        destructive
        loading={cancelMutation.isPending}
        onConfirm={handleCancel}
      />
    </div>
  );
}
